/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Task View Types
 */
export enum TaskViewType {
  ME = 'me',
  ALL = 'all',
  COMPLETED = 'completed',
  ALL_OPENED = 'all-opened'
}

/**
 * Task Status Enum
 */
export enum TaskStatus {
  OPEN = 'OPEN',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  SUSPENDED = 'SUSPENDED'
}

/**
 * Workflow Status Enum
 */
export enum WorkflowStatus {
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

/**
 * FSI (File/Folder) Type Enum
 */
export enum FsiType {
  DOCUMENT = 'DOCUMENT',
  FOLDER = 'FOLDER',
  PLACEHOLDER = 'PLACEHOLDER',
  REFERENCE = 'REFERENCE'
}

/**
 * Task Type Enum
 */
export enum TaskType {
  USER = 'USER',
  USER_REJECTED_TASK = 'USER_REJECTED_TASK',
  SYSTEM = 'SYSTEM'
}

/**
 * Workflow Type Enum
 */
export enum WorkflowType {
  NEW = 'NEW',
  CHANGE = 'CHANGE',
  OBSOLETE = 'OBSOLETE',
  OBSOLETE_REFERENCE = 'OBSOLETE_REFERENCE',
  OBSOLETE_PLACEHOLDER = 'OBSOLETE_PLACEHOLDER',
  TRAINING = 'TRAINING',
  IMPORT = 'IMPORT',
  CREATE_REFERENCE = 'CREATE_REFERENCE',
  CREATE_PLACEHOLDER = 'CREATE_PLACEHOLDER',
  FILL_PLACEHOLDER = 'FILL_PLACEHOLDER',
  FILE_ARTIFACT = 'FILE_ARTIFACT',
  RECLASSIFY_DOCUMENT = 'RECLASSIFY_DOCUMENT',
  EOS = 'EOS',
  EOS_DOWNLOAD_AND_ACKNOWLEDGE = 'EOS_DOWNLOAD_AND_ACKNOWLEDGE',
  QC_REVIEW = 'QC_REVIEW'
}

/**
 * Workflow Step Type Enum
 */
export enum WorkflowStepType {
  AUTHORIZE_PLACEHOLDER_CREATION = 'AUTHORIZE_PLACEHOLDER_CREATION',
  AUTHORIZE_REFERENCE_CREATION = 'AUTHORIZE_REFERENCE_CREATION',
  AUTHORIZE_MIGRATION = 'AUTHORIZE_MIGRATION',
  AUTHORIZE_FILL_PLACEHOLDER = 'AUTHORIZE_FILL_PLACEHOLDER',
  AUTHORIZE_RECLASSIFICATION = 'AUTHORIZE_RECLASSIFICATION',
  AUTHORIZE_CHANGE_REQUEST = 'AUTHORIZE_CHANGE_REQUEST',
  APPROVE_OBSOLETE = 'APPROVE_OBSOLETE',
  DOWNLOAD_EOS = 'DOWNLOAD_EOS',
  REVISE_EDIT = 'REVISE_EDIT',
  REVISE_EDIT_MULTIFILE = 'REVISE_EDIT_MULTIFILE',
  REVISE_EDIT_EOS = 'REVISE_EDIT_EOS',
  REVISE_EDIT_MIGRATION = 'REVISE_EDIT_MIGRATION',
  APPROVE_REFERENCE_CREATION = 'APPROVE_REFERENCE_CREATION',
  APPROVE_PLACEHOLDER_CREATION = 'APPROVE_PLACEHOLDER_CREATION',
  APPROVE_OBSOLETING_REFERENCE = 'APPROVE_OBSOLETING_REFERENCE',
  REVISE_EDIT_RECLASSIFICATION = 'REVISE_EDIT_RECLASSIFICATION',
  REVISE_EDIT_FILE_ARTIFACT = 'REVISE_EDIT_FILE_ARTIFACT',
  ACKNOWLEDGE_EOS = 'ACKNOWLEDGE_EOS',
  REVIEW_QC = 'REVIEW_QC',
  APPROVE_QC = 'APPROVE_QC',
  REVIEW = 'REVIEW',
  REVIEW_MULTIFILE = 'REVIEW_MULTIFILE',
  REVIEW_EOS = 'REVIEW_EOS',
  REVIEW_RECLASSIFICATION = 'REVIEW_RECLASSIFICATION',
  REVIEW_FILE_ARTIFACT = 'REVIEW_FILE_ARTIFACT',
  APPROVE_MULTIFILE = 'APPROVE_MULTIFILE',
  APPROVE_MIGRATION = 'APPROVE_MIGRATION',
  APPROVE = 'APPROVE',
  APPROVE_EOS = 'APPROVE_EOS',
  APPROVE_RECLASSIFICATION = 'APPROVE_RECLASSIFICATION',
  APPROVE_FILE_ARTIFACT = 'APPROVE_FILE_ARTIFACT',
  TRAINING = 'TRAINING'
}

/**
 * User Role Enum
 */
export enum Role {
  ADMIN = 'ADMIN',
  USER = 'USER',
  GUEST = 'GUEST'
}

/**
 * Task Filter Interface
 */
export interface TaskFilterState {
  taskTypesCode: string[]
  userId: number[]
  workflowInitiatorId: number[]
  workflowId: Array<{ value: number; label: string }>
  startFrom: Date | null
  startTo: Date | null
  study: string | null
  site: string | null
  isEosSelected: boolean
  isEosEnabled: boolean
  isEtmfEnabled: boolean
  data: {
    product: { fsiId: string; fsiName: string } | null
    study: Array<{ fsiId: string; fsiName: string }> | null
    environment: { fsiId: string; fsiName: string } | null
    country: { fsiId: string; fsiName: string } | null
    site: { fsiId: string; fsiName: string } | null
  }
  folderId: { id: string; name: string } | null
}

/**
 * API Filter Parameters
 */
export interface TasksApiFilter {
  taskTypesCode?: string[]
  userId?: number[]
  workflowInitiatorId?: number[]
  workflowId?: number[]
  startFrom?: number // Milliseconds timestamp
  startTo?: number // Milliseconds timestamp
  studyFolderId?: string
  siteFolderId?: string
  fsiIds?: string[]
  taskStatus?: TaskStatus[] | TaskStatus
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
}

/**
 * Select Option Interface
 */
export interface SelectOption<T = any> {
  value: T
  label: string
  [key: string]: any
}

/**
 * Tasks Table Props
 */
export interface TasksTableProps {
  viewType: TaskViewType
  filterShown: boolean
  currentUserId?: number
  isEosEnabled?: boolean
}

/**
 * Task Filter Props
 */
export interface TaskFilterProps {
  viewType: TaskViewType
}

/**
 * EOS Task Types
 */
export const EOS_TASK_TYPES = ['ACKNOWLEDGE_EOS', 'DOWNLOAD_EOS'] as const

/**
 * ETMF Report Levels
 */
export enum EtmfLevel {
  PRODUCT = 'PRODUCT',
  STUDY = 'STUDY',
  ENVIRONMENT = 'ENVIRONMENT',
  COUNTRY = 'COUNTRY',
  SITE = 'SITE'
}

// Re-export Task type from API types
export type { Task, TaskFlags } from '../../services/api/types'
